import { useState, useMemo, useCallback } from 'react';

// Client-side search and filtering for a table that already holds all its rows.
//
// ONLY FOR TABLES THAT FETCH EVERYTHING. A screen that pages on the server must search on
// the server too — filtering the rows already in the browser would search one page, so
// looking for something on page 7 would confidently report it does not exist. Tenants,
// invoices, payments and landlord payments all page, and all search server-side.
//
//   const { query, setQuery, filterValues, setFilter, reset, rows, isFiltering } =
//     useTableSearch(properties, {
//       searchFields: ['propertyName', 'addressStreet', 'landlord.name'],
//       filters: {
//         status: (row, value) => row.status === value,
//         landlordId: (row, value) => String(row.landlord?._id) === value
//       }
//     });

/** Reads 'landlord.name' off a row, tolerating a missing middle. */
function valueAt(row, path) {
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), row);
}

export default function useTableSearch(rows, { searchFields = [], filters = {} } = {}) {
  const [query, setQuery] = useState('');
  const [filterValues, setFilterValues] = useState({});

  const setFilter = useCallback((key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => {
    setQuery('');
    setFilterValues({});
  }, []);

  const filtered = useMemo(() => {
    const source = Array.isArray(rows) ? rows : [];
    const needle = query.trim().toLowerCase();

    return source.filter((row) => {
      // Every active filter must pass; an unset filter is not a filter.
      for (const [key, predicate] of Object.entries(filters)) {
        const value = filterValues[key];
        if (value === undefined || value === null || value === '') continue;
        if (!predicate(row, value)) return false;
      }

      if (!needle) return true;

      // Any field matching is enough — someone searching "0772" wants the tenant whose
      // phone contains it, without having to say which column to look in.
      return searchFields.some((field) => {
        const value = valueAt(row, field);
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(needle);
      });
    });
  }, [rows, query, filterValues, searchFields, filters]);

  const isFiltering = Boolean(query.trim())
    || Object.values(filterValues).some((v) => v !== '' && v !== null && v !== undefined);

  return {
    query,
    setQuery,
    filterValues,
    setFilter,
    reset,
    rows: filtered,
    isFiltering,
    totalCount: Array.isArray(rows) ? rows.length : 0,
    resultCount: filtered.length
  };
}
